<template>
  <div class="auth-page">
    <div class="auth-card">
      <h1 class="auth-title">Skapa konto</h1>

      <div class="oauth-buttons">
        <a :href="googleUrl" class="oauth-btn">
          <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
            <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
            <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
            <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
            <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
          </svg>
          Fortsätt med Google
        </a>
      </div>

      <div class="divider"><span>eller</span></div>

      <q-form @submit="register" class="auth-form">
        <q-input
          v-model="name"
          label="Namn"
          outlined
          autocomplete="name"
        />

        <q-input
          v-model="email"
          label="E-post"
          outlined
          type="email"
          autocomplete="email"
          class="q-mt-md"
          :rules="[(val) => !!val || 'E-post är obligatoriskt']"
        />

        <q-input
          v-model="password"
          label="Lösenord (minst 8 tecken)"
          outlined
          type="password"
          autocomplete="new-password"
          class="q-mt-md"
          :rules="[
            (val) => !!val || 'Lösenord är obligatoriskt',
            (val) => val.length >= 8 || 'Minst 8 tecken',
          ]"
        />

        <q-btn
          label="Skapa konto"
          type="submit"
          color="primary"
          class="q-mt-md full-width"
          :loading="loading"
        />
      </q-form>

      <p class="auth-policy">
        Genom att skapa ett konto godkänner du vår
        <router-link to="/policy" target="_blank">integritets- och användarpolicy</router-link>.
      </p>

      <p class="auth-footer">
        Har du redan konto?
        <router-link to="/login">Logga in</router-link>
      </p>
    </div>
  </div>
</template>

<script>
import { ref } from "vue";
import { useQuasar } from "quasar";
import axios from "axios";
import { useRouter } from "vue-router";
import { setToken } from "@/utils/auth";

export default {
  name: "RegisterPage",
  setup() {
    const name = ref("");
    const email = ref("");
    const password = ref("");
    const loading = ref(false);
    const $q = useQuasar();
    const router = useRouter();

    const apiBase = process.env.VUE_APP_API_URL.replace(/\/$/, "");
    const googleUrl = `${apiBase}/auth/google`;

    const register = async () => {
      loading.value = true;
      try {
        const { data } = await axios.post(
          process.env.VUE_APP_API_URL + "auth/register",
          {
            email: email.value,
            password: password.value,
            name: name.value,
          }
        );
        await setToken(data.token);
        $q.notify({ type: "positive", message: "Välkommen!" });
        router.push("/");
      } catch (error) {
        $q.notify({
          type: "negative",
          message:
            error.response?.data?.message || "Kunde inte skapa konto",
        });
      } finally {
        loading.value = false;
      }
    };

    return { name, email, password, loading, register, googleUrl };
  },
};
</script>

<style scoped>
.auth-page {
  min-height: calc(100vh - 84px);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem 1rem;
}
.auth-card {
  width: 100%;
  max-width: 420px;
  padding: 2.5rem;
  background: var(--color-bg);
  border: 1px solid var(--color-border);
  border-radius: 8px;
}
.auth-title {
  font-family: "Playfair Display", serif;
  font-size: 2rem;
  font-weight: 900;
  margin: 0 0 0.25rem;
}
.auth-sub {
  color: var(--color-text-secondary);
  font-size: 0.9rem;
  margin: 0 0 2rem;
}
.oauth-buttons {
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
  margin-bottom: 1.5rem;
}
.oauth-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.6rem;
  padding: 0.7rem 1rem;
  border: 1px solid var(--color-border);
  border-radius: 6px;
  font-size: 0.9rem;
  font-weight: 500;
  text-decoration: none;
  color: var(--color-text);
  background: var(--color-bg);
  transition: background 0.15s ease, border-color 0.15s ease;
}
.oauth-btn:hover {
  background: rgba(0, 0, 0, 0.03);
  border-color: var(--color-text-secondary);
}
.oauth-btn--apple {
  background: #000;
  color: #fff;
  border-color: #000;
}
.oauth-btn--apple:hover { background: #1a1a1a; }
.divider {
  text-align: center;
  position: relative;
  margin: 1.5rem 0 1.25rem;
  color: var(--color-text-secondary);
  font-size: 0.8rem;
  text-transform: uppercase;
  letter-spacing: 0.1em;
}
.divider::before, .divider::after {
  content: "";
  position: absolute;
  top: 50%;
  width: 40%;
  height: 1px;
  background: var(--color-border);
}
.divider::before { left: 0; }
.divider::after { right: 0; }
.divider span {
  background: var(--color-bg);
  padding: 0 0.75rem;
  position: relative;
  z-index: 1;
}
.auth-form { display: flex; flex-direction: column; }
.auth-policy {
  text-align: center;
  font-size: 0.78rem;
  color: var(--color-text-secondary);
  margin: 1rem 0 0;
  line-height: 1.5;
}

.auth-policy a {
  color: var(--color-text-secondary);
  text-decoration: underline;
}

.auth-policy a:hover {
  color: var(--color-accent);
}

.auth-footer {
  text-align: center;
  font-size: 0.875rem;
  color: var(--color-text-secondary);
  margin: 1.5rem 0 0;
}
.auth-footer a {
  color: var(--color-accent);
  font-weight: 600;
  text-decoration: none;
}
.auth-footer a:hover { text-decoration: underline; }
</style>
